/**
 * Call Off API module - endpoints for call-off management, stock planning,
 * part repository, and supplier stock.
 */
import api, { type AxiosResponse } from './client';
import type {
  CallOff,
  CallOffStats,
  CallOffEditHistoryEntry,
  CallOffMessage,
  CallOffTimelineEvent,
  CallOffDelivery,
  CallOffLine,
  CreateCallOffRequest,
  BatchInlineEditChange,
  AvailablePartForCallOff,
  CallOffDeliveryDestination,
  BulkConfirmResult,
  StockPlanningResponse,
  ColumnPreference,
  ColumnOption,
  PartRepositoryResponse,
  PartSupplierAssignment,
  PartRepositoryStats,
  SupplierStockItem,
  StockChangeLogEntry,
  StockUploadResult,
  DirectDeliveryOrder,
  CallOffCustomerConfig,
  CallOffDirectDeliverySource,
  QueryParams,
} from './types';

export const callOffAPI = {
  // ---------------------------------------------------------------------------
  // Call-Off CRUD + Lifecycle
  // ---------------------------------------------------------------------------

  /** List call-offs with filters and pagination */
  getCallOffs: (params?: QueryParams): Promise<AxiosResponse<{ results: CallOff[]; count: number }>> =>
    api.get('/call-off/call-offs/', { params }),

  /** Get call-off details with lines */
  getCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.get(`/call-off/call-offs/${id}/`),

  /** Create a new call-off */
  createCallOff: (data: CreateCallOffRequest): Promise<AxiosResponse<CallOff>> =>
    api.post('/call-off/call-offs/', data),

  /** Update a call-off */
  updateCallOff: (id: number, data: Partial<CreateCallOffRequest>): Promise<AxiosResponse<CallOff>> =>
    api.patch(`/call-off/call-offs/${id}/`, data),

  /** Send call-off to supplier (draft → pending) */
  sendCallOff: (id: number, skipValidation?: boolean): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/send/`, { skip_validation: skipValidation }),

  /** Confirm call-off (pending → confirmed) */
  confirmCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/confirm/`),

  /** Dispatch call-off (confirmed → dispatched) */
  dispatchCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/dispatch/`),

  /** Mark call-off as delivered (dispatched → delivered) */
  deliverCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/deliver/`),

  /** Cancel call-off (draft/pending/confirmed → cancelled). Reason required once visible to supplier. */
  cancelCallOff: (id: number, reason?: string): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/cancel/`, { reason: reason || '' }),

  /** Bulk confirm multiple call-offs.
   *  The backend confirms in PO-window order rather than the order sent, so the
   *  batch allocates exactly as the same call-offs would one at a time, and
   *  reports any line it could not fully place on a PO. */
  bulkConfirm: (callOffIds: number[]): Promise<AxiosResponse<BulkConfirmResult>> =>
    api.post('/call-off/call-offs/bulk-confirm/', { call_off_ids: callOffIds }),

  /** Get call-off status statistics */
  getCallOffStats: (): Promise<AxiosResponse<CallOffStats>> =>
    api.get('/call-off/call-offs/stats/'),

  /** Get call-off timeline events */
  getTimeline: (id: number): Promise<AxiosResponse<CallOffTimelineEvent[]>> =>
    api.get(`/call-off/call-offs/${id}/timeline/`),

  /** Get call-off edit history */
  getEditHistory: (id: number): Promise<AxiosResponse<CallOffEditHistoryEntry[]>> =>
    api.get(`/call-off/call-offs/${id}/edit-history/`),

  /** Get call-off messages */
  getMessages: (id: number): Promise<AxiosResponse<CallOffMessage[]>> =>
    api.get(`/call-off/call-offs/${id}/messages/`),

  /** Send a message on a call-off */
  sendMessage: (id: number, messageText: string): Promise<AxiosResponse<CallOffMessage>> =>
    api.post(`/call-off/call-offs/${id}/messages/`, { message_text: messageText }),

  /** Mark messages from the other party as read */
  markMessagesRead: (id: number): Promise<AxiosResponse<{ marked_read: number }>> =>
    api.post(`/call-off/call-offs/${id}/mark-read/`),

  /** Inline edit a call-off line field */
  inlineEdit: (id: number, lineId: number, field: string, value: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/call-off/call-offs/${id}/inline-edit/`, { line_id: lineId, field, value }),

  /** Batch inline edit multiple lines (supplier flow) */
  batchInlineEdit: (id: number, changes: BatchInlineEditChange[]): Promise<AxiosResponse<{ status: string; changes_applied: number }>> =>
    api.post(`/call-off/call-offs/${id}/batch-inline-edit/`, { changes }),

  /** Add new lines to a call-off */
  addLines: (id: number, lines: Record<string, unknown>[]): Promise<AxiosResponse<CallOffLine[]>> =>
    api.post(`/call-off/call-offs/${id}/add-lines/`, { lines }),

  /** Get available parts for the call-off's supplier */
  getAvailableParts: (id: number): Promise<AxiosResponse<AvailablePartForCallOff[]>> =>
    api.get(`/call-off/call-offs/${id}/available-parts/`),

  /** Get PO availability per warehouse part on this call-off.
   *  Scoped to call-off's customer/supplier, so suppliers get correct data. */
  getCallOffAvailability: (id: number): Promise<AxiosResponse<Record<string, string>>> =>
    api.get(`/call-off/call-offs/${id}/availability/`),

  /** Get delivery destinations for a call-off */
  getAvailableDeliveries: (id: number): Promise<AxiosResponse<CallOffDeliveryDestination[]>> =>
    api.get(`/call-off/call-offs/${id}/available-deliveries/`),

  /** Get delivery records (ASNs) for a call-off */
  getDeliveries: (id: number): Promise<AxiosResponse<CallOffDelivery[]>> =>
    api.get(`/call-off/call-offs/${id}/deliveries/`),

  /** Get suppliers with assigned parts */
  getSuppliers: (): Promise<AxiosResponse<{ id: number; name: string; supplier_code: string }[]>> =>
    api.get('/call-off/call-offs/suppliers/'),

  /** Get supplier statistics */
  getSupplierStats: (): Promise<AxiosResponse<{ supplier__id: number; supplier__name: string; total: number; active: number }[]>> =>
    api.get('/call-off/call-offs/supplier-stats/'),

  /** Get available warehouses */
  getWarehouses: (): Promise<AxiosResponse<{ id: number; name: string; code: string; warehouse_type: string }[]>> =>
    api.get('/call-off/call-offs/warehouses/'),

  /** Get direct delivery orders (grouped PolishFactoryItems by sales_order) */
  getDirectDeliveryOrders: (params?: QueryParams): Promise<AxiosResponse<DirectDeliveryOrder[]>> =>
    api.get('/call-off/direct-delivery-orders/', { params }),

  // ---------------------------------------------------------------------------
  // Stock Planning
  // ---------------------------------------------------------------------------

  /** Get stock planning grid data */
  getStockPlanning: (params?: QueryParams): Promise<AxiosResponse<StockPlanningResponse>> =>
    api.get('/call-off/stock-planning/', { params }),

  /** Get all available column definitions */
  getColumnOptions: (): Promise<AxiosResponse<ColumnOption[]>> =>
    api.get('/call-off/stock-planning/column-options/'),

  /** Distinct Polish Factory values for the factory filter dropdown */
  getStockPlanningFactories: (): Promise<AxiosResponse<string[]>> =>
    api.get('/call-off/stock-planning/factories/'),

  /** Get user's column preferences */
  getColumns: (): Promise<AxiosResponse<ColumnPreference[]>> =>
    api.get('/call-off/stock-planning/columns/'),

  /** Toggle column visibility */
  toggleColumn: (columnId: string): Promise<AxiosResponse<ColumnPreference>> =>
    api.post('/call-off/stock-planning/toggle-column/', { column_id: columnId }),

  /** Update column sort order */
  sortColumns: (columns: { column_id: string; sort_order: number }[]): Promise<AxiosResponse<ColumnPreference[]>> =>
    api.post('/call-off/stock-planning/sort-columns/', { columns }),

  /** Export the stock planning grid as CSV or XLSX. Returns a Blob the
   *  caller can save via an object URL. Capped at 10K rows server-side. */
  exportStockPlanning: (params: {
    file_format: 'csv' | 'xlsx';
    customer_id?: number;
    supplier_id?: number;
    columns?: string;
    open_po_days?: number;
    sal_committed_end_date?: string;
    factory_filter?: string;
    commodity_type?: string;
  }): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/stock-planning/export/', {
      params,
      responseType: 'blob',
    }),

  // ---------------------------------------------------------------------------
  // Part Repository
  // ---------------------------------------------------------------------------

  /** List parts with supplier assignments */
  getParts: (params?: QueryParams): Promise<AxiosResponse<PartRepositoryResponse>> =>
    api.get('/call-off/parts/', { params }),

  /** Get part details */
  getPart: (id: number): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/call-off/parts/${id}/`),

  /** Update part details */
  updatePart: (id: number, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/call-off/parts/${id}/`, data),

  /** Get suppliers assigned to a part */
  getPartSuppliers: (id: number): Promise<AxiosResponse<PartSupplierAssignment[]>> =>
    api.get(`/call-off/parts/${id}/suppliers/`),

  /** Assign suppliers to a part (replacement) */
  assignPartSuppliers: (id: number, supplierIds: number[]): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/call-off/parts/${id}/suppliers/`, { supplier_ids: supplierIds }),

  /** Clear all suppliers from a part */
  clearPartSuppliers: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/call-off/parts/${id}/suppliers/`),

  /** Bulk assign suppliers to multiple parts */
  bulkAssignSuppliers: (partIds: number[], supplierIds: number[]): Promise<AxiosResponse<{ created: number }>> =>
    api.post('/call-off/parts/bulk-assign/', { part_ids: partIds, supplier_ids: supplierIds }),

  /** Get part repository statistics */
  getPartStats: (): Promise<AxiosResponse<PartRepositoryStats>> =>
    api.get('/call-off/parts/stats/'),

  // ---------------------------------------------------------------------------
  // Supplier Stock
  // ---------------------------------------------------------------------------

  /** List supplier stock levels */
  getSupplierStock: (params?: QueryParams): Promise<AxiosResponse<{ items: SupplierStockItem[]; total_count: number; page: number; page_size: number }>> =>
    api.get('/call-off/supplier-stock/', { params }),

  /** Save inline stock edit. customer_id is optional — backend resolves it
   * for supplier users via PartSupplierAssignment. */
  saveStock: (data: { item_code: string; quantity: number; customer_id?: number }): Promise<AxiosResponse<{ status: string }>> =>
    api.post('/call-off/supplier-stock/save/', data),

  /** Download stock upload template */
  downloadTemplate: (customerId?: number): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/supplier-stock/template/', { params: customerId ? { customer_id: customerId } : {}, responseType: 'blob' }),

  /** Upload supplier stock Excel file */
  uploadStock: (data: FormData): Promise<AxiosResponse<StockUploadResult>> =>
    api.post('/call-off/supplier-stock/upload/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /** Bulk save multiple stock edits (Save Changes button) */
  bulkSaveStock: (data: { items: Array<{ item_code: string; quantity: number }> }): Promise<AxiosResponse<{ saved: number; errors: string[] }>> =>
    api.post('/call-off/supplier-stock/bulk-save/', data),

  /** Replace warehouse_stock with an absolute value, gated by a mandatory
   *  reason. Used by the Current Stock "Correct" action and Stock Levels
   *  Pencil edit — both are correction flows, not the additive Save flow. */
  correctStock: (data: { item_code: string; quantity: number; reason: string; customer_id?: number }):
    Promise<AxiosResponse<{ status: string; changed: boolean; previous_qty: string; current_qty: string }>> =>
    api.post('/call-off/supplier-stock/correct/', data),

  /**
   * Export supplier warehouse stock levels to Excel (.xlsx) or CSV.
   *
   * Suppliers omit `supplierId` and get their own stock. Customers must pass
   * the supplier whose stock they want (CR-7163 (c)); the backend pins the
   * rows to the requesting customer, so this cannot read another customer's
   * view of the same supplier.
   */
  exportStock: (
    format: 'xlsx' | 'csv' = 'xlsx',
    supplierId?: number | string,
  ): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/supplier-stock/export/', {
      params: {
        file_format: format,
        ...(supplierId ? { supplier_id: supplierId } : {}),
      },
      responseType: 'blob',
    }),

  /** Export stock change history to xlsx (default) or csv. Mirrors the
   *  filtering of getChangeHistory but returns every matching row. */
  exportChangeHistory: (format: 'xlsx' | 'csv' = 'xlsx', params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/supplier-stock/change-history/export/', {
      params: { ...(params || {}), file_format: format },
      responseType: 'blob',
    }),

  /** Get stock change history. Pass `item_code` to scope to a single part —
   *  the response then includes a `stats` block (current_total, inputs_30d,
   *  dispatches_30d, last_input_at) used by the per-part history drawer. */
  getChangeHistory: (params?: QueryParams): Promise<AxiosResponse<{
    items: StockChangeLogEntry[];
    total_count: number;
    page: number;
    page_size: number;
    stats?: {
      current_total: string | null;
      inputs_30d: string;
      dispatches_30d: string;
      last_input_at: string | null;
    };
  }>> =>
    api.get('/call-off/supplier-stock/change-history/', { params }),

  /** Time-ordered FIFO allocate / consume / restore events for one part
   *  (StockPOAllocation ledger). Powers the FIFO drill-down drawer on
   *  the Stock Upload → Change History sub-tab. */
  getAllocationHistory: (itemCode: string): Promise<AxiosResponse<{
    items: Array<{
      id: number;
      event_type: 'allocate' | 'consume' | 'restore';
      qty: string;
      po_number: string | null;
      po_line_number: number | null;
      po_line_id: number | null;
      asn_number: string | null;
      created_at: string;
    }>;
  }>> =>
    api.get('/call-off/supplier-stock/allocation-history/', {
      params: { item_code: itemCode },
    }),

  // ---------------------------------------------------------------------------
  // Sub-tabs
  // ---------------------------------------------------------------------------

  /** Get stock status list */
  getStockStatus: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/stock-status/', { params }),

  /** Get open PO list */
  getOpenPO: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/open-po/', { params }),

  /** Get SAL committed list */
  getSalCommitted: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/sal-committed/', { params }),

  /** Get Polish Factory list */
  getPolishFactory: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/polish-factory/', { params }),

  /** Get min/max stock levels */
  getMinMax: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/min-max/', { params }),

  /** Get forecast list */
  getForecasts: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/forecasts/', { params }),

  /** Get Poland stock list */
  getPolandStock: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/poland-stock/', { params }),

  /** Export the Poland Stock sub-tab as CSV or XLSX. Honors the active
   *  search filter and part-number sort; returns ALL matching rows
   *  (unpaginated) as a Blob the caller saves via an object URL. */
  exportPolandStock: (params: {
    file_format: 'csv' | 'xlsx';
    search?: string;
    ordering?: string;
  }): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/poland-stock/export/', { params, responseType: 'blob' }),

  /** Get in-transit list */
  getInTransit: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/in-transit/', { params }),

  // ---------------------------------------------------------------------------
  // Customer Configuration (templating)
  // ---------------------------------------------------------------------------

  /** Get the active Call-Off configuration for the current user's customer.
   *  Always returns a payload — defaults match historical hardcoded values
   *  when no row exists. */
  getCustomerConfig: (): Promise<AxiosResponse<CallOffCustomerConfig>> =>
    api.get('/call-off/customer-config/current/'),

  /** Helix-admin only: list all customer configs */
  listCustomerConfigs: (): Promise<AxiosResponse<{ results: CallOffCustomerConfig[]; count: number }>> =>
    api.get('/call-off/customer-config/'),

  /** Helix-admin only: update a customer config */
  updateCustomerConfig: (id: number, data: Partial<CallOffCustomerConfig>): Promise<AxiosResponse<CallOffCustomerConfig>> =>
    api.patch(`/call-off/customer-config/${id}/`, data),

  /** Helix-admin only: add a Direct Delivery source */
  addDirectDeliverySource: (configId: number, data: Partial<CallOffDirectDeliverySource>): Promise<AxiosResponse<CallOffDirectDeliverySource>> =>
    api.post(`/call-off/customer-config/${configId}/sources/`, data),

  /** Helix-admin only: update a Direct Delivery source */
  updateDirectDeliverySource: (configId: number, sourceId: number, data: Partial<CallOffDirectDeliverySource>): Promise<AxiosResponse<CallOffDirectDeliverySource>> =>
    api.patch(`/call-off/customer-config/${configId}/sources/${sourceId}/`, data),

  /** Helix-admin only: delete a Direct Delivery source */
  deleteDirectDeliverySource: (configId: number, sourceId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/call-off/customer-config/${configId}/sources/${sourceId}/`),

  /** Helix-admin only: reorder Direct Delivery sources by ID */
  reorderDirectDeliverySources: (configId: number, order: number[]): Promise<AxiosResponse<CallOffCustomerConfig>> =>
    api.post(`/call-off/customer-config/${configId}/sources/reorder/`, { order }),
};
